import { useState } from 'react'

import {
  type DocumentVersion,
  type LibraryDocument,
  type LibraryFolder,
  canBeWrittenIn,
  contentUrl,
  previewUrl,
} from '../../api/library'
import { asFailure, type Failure } from '../../api/request'
import { whyItMightNotBeFound } from '../../api/search'
import { Button, LinkButton } from '../../ui/Control'
import { Done, Failed } from '../../ui/Status'
import { useAnnounce } from '../../ui/Announce'
import { LiesOn } from './LiesOn'
import { Origins, Versions } from './Origins'
import { asDate, asSize } from './Tiles'
import { destinationsAmong } from './place'
import { WriteDocument } from './WriteDocument'

/**
 * One document, open.
 *
 * It shows the document rather than describing it: the file itself where a browser can show one, and
 * the picture of its first page where it cannot. Everything that can be done to it is a visible control
 * here — renaming, putting it somewhere, downloading it, deleting it — which is what makes dragging a
 * tile onto a folder a shortcut rather than the only way.
 *
 * Deleting moves it to the trash and asks nothing. The one thing in the library that asks is emptying
 * the trash, because that is the one thing that cannot be undone.
 *
 * **On an iPhone and an iPad, an embedded PDF is not the whole story.** Safari renders one in a frame
 * but does not scroll it the way a page scrolls, so a long script is a first page and no more. That is
 * why "Open on its own" sits beside the frame at all times and is not tucked away: on a phone it is
 * the way to actually read the thing, and on a desktop it is the way to put it on a second screen.
 */
export interface OpenDocumentProps {
  readonly document: LibraryDocument
  readonly folders: readonly LibraryFolder[]
  readonly onRename: (title: string) => Promise<void>
  readonly onMove: (folderId: string | null) => Promise<void>
  /** Moves it to the trash. Nothing is asked — that is what the trash is for. */
  readonly onDelete: () => Promise<void>
  /** Every version of it, newest first. Empty until they have been fetched, or when there is one. */
  readonly versions: readonly DocumentVersion[]
  /** Hands in a newer file for it, which is what a sync of the same material does. */
  readonly onNewVersion: (file: File) => Promise<void>
  /** Closes the document again — the same thing the frame's back control does. */
  readonly onClose: () => void
  /** Called after the text was written, so the shelf and the tile follow what the document now is. */
  readonly onChanged: () => void
  /**
   * Whether the document is open with nothing else beside it.
   *
   * Writing is the case this exists for: the shelf, the download, the history and the folder picker
   * are all things somebody is *not* doing while they write, and on a wide screen they cost the words
   * half the width. So they go, and the way back is one control and the frame's own back.
   */
  readonly alone?: boolean
  /** Asks for that, or for the way back out of it. Absent where it cannot be offered. */
  readonly onAlone?: (alone: boolean) => void
}

export function OpenDocument({
  document,
  folders,
  versions,
  onRename,
  onMove,
  onDelete,
  onNewVersion,
  onClose,
  onChanged,
  alone = false,
  onAlone,
}: OpenDocumentProps) {
  const announce = useAnnounce()
  const [title, setTitle] = useState(document.title)
  const [saved, setSaved] = useState<number | null>(null)
  const [failure, setFailure] = useState<Failure | null>(null)

  const rename = async () => {
    const wanted = title.trim()
    if (wanted === '' || wanted === document.title) {
      setTitle(document.title)
      return
    }
    try {
      await onRename(wanted)
      setSaved(Date.now())
      setFailure(null)
    } catch (error) {
      setTitle(document.title)
      setFailure(asFailure(error))
    }
  }

  const place = async (folderId: string | null) => {
    try {
      await onMove(folderId)
      announce(folderId === null ? 'Moved to the top of the library.' : 'Moved.')
      setFailure(null)
    } catch (error) {
      setFailure(asFailure(error))
    }
  }

  return (
    <article
      className="sheet"
      // Escape closes it. The title field stops the key before it gets here, so escaping out of a
      // half-typed title puts the old one back rather than throwing the whole document off the screen.
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          onClose()
        }
      }}
    >
      <header className="sheet__head">
        {/*
          The way out, at the top. It used to be a "Close" button in the row of controls *below* the
          document — and the document is up to 70% of the window tall, so the way out was under the fold
          and had to be scrolled to. A thing that closes something belongs where the thing begins.
        */}
        <Button
          tone="bare"
          className="sheet__close"
          icon="close"
          iconOnlyLabel="Close this document"
          onClick={onClose}
        />
        {/*
          The title is edited where it stands rather than in a form somewhere else: it is saved when
          the field is left or Enter is pressed, and the confirmation appears on the object itself.
        */}
        <label className="sheet__title-label" htmlFor="document-title">
          Title
        </label>
        <input
          id="document-title"
          className="sheet__title"
          value={title}
          onChange={(event) => {
            setTitle(event.target.value)
          }}
          onBlur={() => {
            void rename()
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.currentTarget.blur()
            }
            if (event.key === 'Escape') {
              event.stopPropagation()
              setTitle(document.title)
            }
          }}
        />
        <p className="sheet__facts">
          <span>{document.kind === '' ? 'File' : document.kind}</span>
          <span aria-hidden="true">·</span>
          <span>{asSize(document.sizeInBytes)}</span>
          <span aria-hidden="true">·</span>
          <span>{asDate(document.takenOverAt)}</span>
          {document.hasEarlierVersions && (
            <>
              <span aria-hidden="true">·</span>
              <span>version {document.version}</span>
            </>
          )}
          {saved !== null && <Done key={saved} label="Saved" />}
        </p>
        {/*
          Where it came from, and — where that applies — that the file has disappeared there. The
          document stays and stays usable; this is a mark on it and never a reason to remove it. MyLib
          is somebody's own archive, not a mirror of somebody else's.
        */}
        <Origins origins={document.origins} />

        {/*
          And where it lies *on top of* the library: the boards somebody has laid a card of it on.
          The way back up to the desk from here, and the only one there is — a card is a reference,
          so the document itself knows nothing about who put it where.
        */}
        <LiesOn kind="document" id={document.id} />

        {/*
          Why this document might not turn up in a search — said here, where somebody is looking at it,
          and only when there is something to say. A scan with no text layer that is silently missing
          from a result is the worst thing a search can do to somebody relying on it; the sentence costs
          a line and turns "it did not find my document" into "ah, it cannot read this one yet".
        */}
        {whyItMightNotBeFound(document) !== null && (
          <p className="sheet__aside">{whyItMightNotBeFound(document)}</p>
        )}
      </header>

      {failure !== null && <Failed failure={failure} />}

      {/*
        A text is opened in the editor and everything else is shown as what it is. It is the *document*
        that decides which — its format, which the service works out from the file — and never the
        screen guessing from a name.
      */}
      {canBeWrittenIn(document) ? (
        <WriteDocument document={document} onChanged={onChanged} full={alone} onFull={onAlone} />
      ) : (
        <View document={document} />
      )}

      {/*
        Everything below is about the document rather than about what it says — downloading it, its
        history, where it is kept, throwing it away. While somebody is writing, none of it is what
        they came for, and on a wide screen all of it is between them and the next paragraph.
      */}
      {alone ? null : (
        <>
          <div className="sheet__doing">
            <LinkButton
              tone="primary"
              href={contentUrl(document, { download: true })}
              download={document.fileName}
              rel="noreferrer"
            >
              Download
            </LinkButton>
            {document.canBeShownInBrowser && (
              <LinkButton href={contentUrl(document)} target="_blank" rel="noreferrer">
                Open on its own
              </LinkButton>
            )}
          </div>

          <Versions document={document} versions={versions} />

          <div className="sheet__setting">
            <label className="field">
              <span className="field__label">Newer file for this document</span>
              <input
                className="field__input field__input--file"
                type="file"
                onChange={(event) => {
                  const chosen = event.target.files?.[0]
                  event.target.value = ''
                  if (chosen !== undefined) {
                    void onNewVersion(chosen).catch((error: unknown) => {
                      setFailure(asFailure(error))
                    })
                  }
                }}
              />
            </label>
            <span className="sheet__aside">
              It becomes another version of this document. The one before it stays readable.
            </span>
          </div>

          <div className="sheet__setting">
            <label className="field">
              <span className="field__label">Keep it in</span>
              <select
                className="field__input"
                value={document.folderId ?? ''}
                onChange={(event) => {
                  void place(event.target.value === '' ? null : event.target.value)
                }}
              >
                {/* No folder at all: the document lies at the top of the library. */}
                <option value="">Root</option>
                {destinationsAmong(folders).map((folder) => (
                  <option key={folder.id} value={folder.id}>
                    {folder.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {/*
        Nothing is asked. It goes to the trash, where it stays until the trash is emptied, and comes back
        from there in one control — so this is an ordinary action like every other one in MyLib, and the
        one place that does ask is the one place that cannot be undone.
      */}
          <div className="sheet__doing sheet__doing--last">
            <Button
              tone="bare"
              className="control--danger"
              onClick={() => {
                void onDelete().catch((error: unknown) => {
                  setFailure(asFailure(error))
                })
              }}
            >
              Move to trash
            </Button>
            <span className="sheet__aside">It waits there until you empty the trash.</span>
          </div>
        </>
      )}
    </article>
  )
}

/**
 * The document itself, as large as the lane allows.
 *
 * A PDF in a frame, a picture as a picture, and for everything else the rendered first page if there is
 * one. The service decides which kinds may be embedded at all — a file it will not show inline is
 * handed back as a download whatever this asks for — so nothing here guesses from a file name.
 */
function View({ document }: { readonly document: LibraryDocument }) {
  const [drawn, setDrawn] = useState(document.hasPreview)

  if (document.canBeShownInBrowser && document.mediaType.startsWith('image/')) {
    return (
      <div className="sheet__view sheet__view--image">
        <img src={contentUrl(document)} alt={document.title} />
      </div>
    )
  }

  if (document.canBeShownInBrowser) {
    return (
      <div className="sheet__view">
        <iframe className="sheet__frame" src={contentUrl(document)} title={document.title} />
      </div>
    )
  }

  if (drawn) {
    return (
      <div className="sheet__view sheet__view--image">
        <img
          src={previewUrl(document)}
          alt=""
          onError={() => {
            setDrawn(false)
          }}
        />
        <p className="sheet__note">
          This kind of file is not shown in the browser. The picture is its first page; download it
          to work with it.
        </p>
      </div>
    )
  }

  return (
    <div className="sheet__view sheet__view--none">
      <p className="sheet__instead">{document.kind === '' ? 'FILE' : document.kind}</p>
      <p className="sheet__note">
        This kind of file cannot be shown in a browser. It is kept exactly as it arrived — download
        it to open it where it belongs.
      </p>
    </div>
  )
}
